import { api } from "./api";

export type DecisionStatus = "open" | "reviewed";
export type DecisionOutcome = "confirmed" | "changed" | "invalidated";
export type FindingAssessment = "useful" | "not_useful" | "unclear";
export type DecisionAssumptionAggregation =
  | "sum"
  | "average"
  | "count"
  | "minimum"
  | "maximum";
export type DecisionAssumptionOperator = "equals" | "at_least" | "at_most" | "between";

export interface DecisionAssumption {
  id: string;
  workspace_id: string;
  decision_id: string;
  label: string;
  measure_table: string;
  measure_column: string;
  aggregation: DecisionAssumptionAggregation;
  operator: DecisionAssumptionOperator;
  expected_value: number | null;
  lower_bound: number | null;
  upper_bound: number | null;
  unit: string | null;
  scope_json: Record<string, unknown>;
  period_start: string | null;
  period_end: string | null;
  last_state: "unknown" | "holding" | "breached" | "unavailable" | "inactive";
  last_observed_value: number | null;
  last_data_version: string | null;
  last_evaluated_at: string | null;
  last_error: string | null;
  created_at: string;
  updated_at: string;
}

export interface DecisionAssumptionInput {
  label: string;
  measure_table: string;
  measure_column: string;
  aggregation: DecisionAssumptionAggregation;
  operator: DecisionAssumptionOperator;
  expected_value?: number | null;
  lower_bound?: number | null;
  upper_bound?: number | null;
  unit?: string | null;
  scope?: Record<string, unknown>;
  period_start?: string | null;
  period_end?: string | null;
}

export interface DecisionAssumptionCatalogColumn {
  slug: string;
  name: string;
  display_name: string;
  dtype: string;
  semantic_type: string;
  safe_to_sum: boolean | null;
}

export interface DecisionAssumptionCatalogTable {
  slug: string;
  name: string;
  row_count: number;
  measures: DecisionAssumptionCatalogColumn[];
  dimensions: DecisionAssumptionCatalogColumn[];
}

export interface DecisionAssumptionCatalog {
  runtime_version: string;
  data_version: number;
  tables: DecisionAssumptionCatalogTable[];
}

export interface MeridianDecision {
  id: string;
  workspace_id: string;
  meridian_id: string;
  owner_user_id: string | null;
  reviewed_by_user_id: string | null;
  source_insight_fact_id: string | null;
  title: string;
  rationale: string;
  source_kind: string | null;
  evidence_json: Record<string, unknown>;
  status: DecisionStatus;
  decided_at: string;
  review_at: string | null;
  reviewed_at: string | null;
  outcome: DecisionOutcome | null;
  outcome_note: string | null;
  outcome_evidence_json: Record<string, unknown> | null;
  assumptions: DecisionAssumption[];
  created_at: string;
  updated_at: string;
}

export interface MeridianDecisionReview {
  id: string;
  workspace_id: string;
  decision_id: string;
  reviewer_user_id: string | null;
  outcome: DecisionOutcome;
  note: string;
  evidence_json: Record<string, unknown>;
  reviewed_at: string;
}

export interface ScenarioDecisionObservationMeasure {
  table: string;
  column: string;
  aggregation: "sum" | "average";
  promoted_value: number | null;
  observed_value: number | null;
  absolute_change: number | null;
  relative_change: number | null;
  status: "observed" | "unavailable";
  reason: string | null;
}

export interface ScenarioDecisionObservation {
  decision_id: string;
  status: "observed" | "partial" | "unavailable";
  reason: string | null;
  scope: "decision_owner_entitlements";
  scope_comparability: "same" | "changed" | "unknown";
  promoted_data_version: number | null;
  observed_data_version: number | null;
  observed_at: string;
  measures: ScenarioDecisionObservationMeasure[];
}

export interface ScenarioSuccessCriterion {
  assumption_id: string;
  label: string;
  measure_table: string;
  measure_column: string;
  aggregation: "sum" | "average";
  operator: DecisionAssumptionOperator;
  expected_value: number | null;
  lower_bound: number | null;
  upper_bound: number | null;
  unit: string | null;
  scope?: Record<string, unknown>;
  period_start?: string | null;
  period_end?: string | null;
  observed_value: number | null;
  state: "met" | "missed" | "unavailable";
  reason: string | null;
}

export interface ScenarioSuccessCriteriaReview {
  status: "evaluated" | "partial" | "unavailable";
  reason: string | null;
  evaluated_at: string | null;
  met_count: number;
  missed_count: number;
  unavailable_count: number;
  criteria: ScenarioSuccessCriterion[];
  method_note: string;
}

export interface DecisionRetrospective {
  decision_id: string;
  available: boolean;
  reason: string | null;
  source_kind: string;
  outcome: DecisionOutcome | null;
  outcome_note: string | null;
  observed_evidence: Record<string, unknown>;
  stated_confidence: number | null;
  calibration_eligible: boolean;
  interpretation: string;
  scenario_comparison: ScenarioRetrospective | null;
}

export interface ScenarioRetrospectiveMeasure
  extends ScenarioDecisionObservationMeasure {
  movement: "increased" | "decreased" | "unchanged" | "unavailable";
}

export interface ScenarioRetrospective {
  status: "observed" | "partial" | "unavailable";
  reason: string | null;
  scope_comparability: "same" | "changed" | "unknown";
  promoted_data_version: number | null;
  observed_data_version: number | null;
  observed_at: string | null;
  observed_measure_count: number;
  unavailable_measure_count: number;
  measures: ScenarioRetrospectiveMeasure[];
  success_criteria: ScenarioSuccessCriteriaReview | null;
  method_note: string;
}

export interface DecisionReliabilityGroup {
  source_kind: string;
  reviewed_count: number;
  confirmed_count: number;
  changed_count: number;
  invalidated_count: number;
  calibration_sample_count: number;
  minimum_sample_size: number;
  status: "collecting" | "ready";
  empirical_hold_rate: number | null;
  average_stated_confidence: number | null;
  calibration_gap: number | null;
  direction: "collecting" | "in_line" | "possible_overconfidence" | "possible_underconfidence";
  interpretation: string;
}

export interface ScenarioLearningProfile {
  reviewed_decisions: number;
  confirmed_count: number;
  changed_count: number;
  invalidated_count: number;
  minimum_sample_size: number;
  status: "collecting" | "ready";
  confirmed_rate: number | null;
  changed_rate: number | null;
  invalidated_rate: number | null;
  fully_observed_count: number;
  partially_observed_count: number;
  unavailable_observation_count: number;
  comparable_observation_rate: number | null;
  criteria_review_count: number;
  criteria_evaluable_review_count: number;
  criteria_minimum_sample_size: number;
  criteria_status: "collecting" | "ready";
  criteria_met_count: number;
  criteria_missed_count: number;
  criteria_unavailable_count: number;
  criteria_hit_rate: number | null;
  criteria_interpretation: string;
  interpretation: string;
  method_note: string;
}

export interface DecisionLearningProfile {
  total_decisions: number;
  reviewed_decisions: number;
  confirmed_count: number;
  changed_count: number;
  invalidated_count: number;
  overall: DecisionReliabilityGroup;
  by_source_kind: DecisionReliabilityGroup[];
  scenario_outcomes: ScenarioLearningProfile;
  method_note: string;
}

export interface VarianceCalibrationProfile {
  algorithm_version: string;
  status: "collecting" | "ready";
  minimum_sample_size: number;
  assessed_sample_count: number;
  useful_count: number;
  not_useful_count: number;
  unclear_count: number;
  usefulness_rate: number | null;
  current_minimum_abs_relative_gap: number;
  action: "none" | "keep" | "tighten";
  proposed_minimum_abs_relative_gap: number | null;
  recommendation_fingerprint: string;
  interpretation: string;
  method_note: string;
}

export interface VarianceCalibrationRevision {
  id: string;
  meridian_id: string;
  algorithm_version: string;
  recommendation_fingerprint: string;
  assessed_sample_count: number;
  minimum_abs_relative_gap: number;
  created_at: string;
}

export interface DecisionCreateInput {
  title: string;
  rationale: string;
  source_kind?: string | null;
  evidence?: Record<string, unknown>;
  review_at?: string | null;
  assumptions?: DecisionAssumptionInput[];
}

export interface DecisionFromAttentionInput {
  fact_key: string;
  title: string;
  rationale: string;
  review_at?: string | null;
  assumptions?: DecisionAssumptionInput[];
}

export interface DecisionFromDetectorInput {
  detector_ref: string;
  title: string;
  rationale: string;
  review_at?: string | null;
  assumptions?: DecisionAssumptionInput[];
}

export function listModelDecisions(
  modelId: string,
  status: DecisionStatus | "all" = "all",
): Promise<MeridianDecision[]> {
  return api<MeridianDecision[]>(`/api/v1/models/${modelId}/decisions?status=${status}`);
}

export function fetchDecisionAssumptionCatalog(
  modelId: string,
): Promise<DecisionAssumptionCatalog> {
  return api<DecisionAssumptionCatalog>(
    `/api/v1/models/${modelId}/decisions/assumption-catalog`,
  );
}

export function createModelDecision(
  modelId: string,
  input: DecisionCreateInput,
): Promise<MeridianDecision> {
  return api<MeridianDecision>(`/api/v1/models/${modelId}/decisions`, {
    method: "POST",
    body: input,
  });
}

export function createModelDecisionFromAttention(
  modelId: string,
  input: DecisionFromAttentionInput,
): Promise<MeridianDecision> {
  return api<MeridianDecision>(
    `/api/v1/models/${modelId}/decisions/from-attention`,
    {
      method: "POST",
      body: input,
    },
  );
}

export function createModelDecisionFromDetector(
  modelId: string,
  input: DecisionFromDetectorInput,
): Promise<MeridianDecision> {
  return api<MeridianDecision>(
    `/api/v1/models/${modelId}/decisions/from-detector`,
    {
      method: "POST",
      body: input,
    },
  );
}

export function reviewModelDecision(
  modelId: string,
  decisionId: string,
): Promise<MeridianDecision> {
  return api<MeridianDecision>(`/api/v1/models/${modelId}/decisions/${decisionId}/review`, {
    method: "POST",
    body: {},
  });
}

export function recordModelDecisionReview(
  modelId: string,
  decisionId: string,
  input: {
    outcome: DecisionOutcome;
    note: string;
    evidence?: Record<string, unknown>;
    finding_assessment?: FindingAssessment;
  },
): Promise<{
  decision: MeridianDecision;
  review: MeridianDecisionReview;
}> {
  return api<{
    decision: MeridianDecision;
    review: MeridianDecisionReview;
  }>(`/api/v1/models/${modelId}/decisions/${decisionId}/reviews`, {
    method: "POST",
    body: input,
  });
}

export function listModelDecisionReviews(
  modelId: string,
  decisionId: string,
): Promise<MeridianDecisionReview[]> {
  return api<MeridianDecisionReview[]>(`/api/v1/models/${modelId}/decisions/${decisionId}/reviews`);
}

export function getScenarioDecisionObservation(
  modelId: string,
  decisionId: string,
): Promise<ScenarioDecisionObservation> {
  return api<ScenarioDecisionObservation>(
    `/api/v1/models/${modelId}/decisions/${decisionId}/scenario-observation`,
  );
}

export function fetchDecisionRetrospective(
  modelId: string,
  decisionId: string,
): Promise<DecisionRetrospective> {
  return api<DecisionRetrospective>(
    `/api/v1/models/${modelId}/decisions/${decisionId}/retrospective`,
  );
}

export function fetchDecisionLearning(modelId: string): Promise<DecisionLearningProfile> {
  return api<DecisionLearningProfile>(`/api/v1/models/${modelId}/decisions/learning`);
}

export function fetchVarianceCalibration(
  modelId: string,
): Promise<VarianceCalibrationProfile> {
  return api<VarianceCalibrationProfile>(
    `/api/v1/models/${modelId}/decisions/learning/variance`,
  );
}

export function applyVarianceCalibration(
  modelId: string,
  recommendationFingerprint: string,
): Promise<VarianceCalibrationRevision> {
  return api<VarianceCalibrationRevision>(
    `/api/v1/models/${modelId}/decisions/learning/variance/apply`,
    {
      method: "POST",
      body: { recommendation_fingerprint: recommendationFingerprint },
    },
  );
}
